import { useEffect, useMemo, useRef, useState } from 'react';
import type { APIOrganizationUser } from '@ardoq/api-types';
import type { I18nLocale } from '@ardoq/i18n';
import { scopeDataOperations } from '@ardoq/scope-data';
import {
  type GridApi,
  type GridReadyEvent,
  themeQuartz,
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { registerModules } from '../moduleRegistry';
import { getEmptyInventoryApiScopeData } from '../mergeScopeDataIntoInventoryContext';
import { getReferenceInventoryColumnDefs } from './referenceInventoryColumns';
import { getReferenceServerSideDatasource } from './referenceServerSideDatasource';
import { getReferenceColumnMainMenuItems } from './referenceMainMenuItems';
import { useColumnChooserDismissal } from '../useColumnChooserDismissal';
import type { InventoryAccessControl, InventoryConfig } from '../types';
import { getInventoryGridApi } from '../inventoryGridAPI';
import type {
  FetchNextReferenceRowsBatchFunction,
  ReferenceInventoryColumn,
  ReferenceInventoryCommands,
  ReferenceInventoryContext,
  ReferenceInventoryGridApi,
  ReferenceInventoryRow,
} from './types';

registerModules();

export type ReferenceInventoryProps = {
  initialColumnDefinitions: ReferenceInventoryColumn[];
  getNextRowsBatch: FetchNextReferenceRowsBatchFunction;
  users: APIOrganizationUser[];
  accessControl: InventoryAccessControl;
  config: InventoryConfig;
  commands: ReferenceInventoryCommands;
  i18nLocale: I18nLocale;
  defaultVisibleColumnKeys: readonly string[];
  agGridLocaleText: Record<string, string>;
  registerGridApi: (gridApi: ReferenceInventoryGridApi) => void;
  deregisterGridApi: VoidFunction;
};

const toInitialContext = ({
  users,
  accessControl,
  config,
  i18nLocale,
  defaultVisibleColumnKeys,
}: Pick<
  ReferenceInventoryProps,
  | 'users'
  | 'accessControl'
  | 'config'
  | 'i18nLocale'
  | 'defaultVisibleColumnKeys'
>): ReferenceInventoryContext => {
  const apiScopeData = getEmptyInventoryApiScopeData();
  return {
    apiScopeData,
    scopeData: scopeDataOperations.enhance(apiScopeData),
    users,
    accessControl,
    config,
    i18nLocale,
    defaultVisibleColumnKeys,
  };
};

const patchContext = (
  api: GridApi<ReferenceInventoryRow>,
  patch: Partial<ReferenceInventoryContext>
): void => {
  const context = api.getGridOption('context') as ReferenceInventoryContext;
  api.setGridOption('context', { ...context, ...patch });
};

export const ReferenceInventory = ({
  initialColumnDefinitions,
  getNextRowsBatch,
  users,
  accessControl,
  config,
  commands,
  i18nLocale,
  defaultVisibleColumnKeys,
  agGridLocaleText,
  registerGridApi,
  deregisterGridApi,
}: ReferenceInventoryProps) => {
  const gridApiRef = useRef<GridApi<ReferenceInventoryRow> | undefined>(
    undefined
  );
  const [initialContext] = useState(() =>
    toInitialContext({
      users,
      accessControl,
      config,
      i18nLocale,
      defaultVisibleColumnKeys,
    })
  );
  const { onColumnMenuVisibleChanged } = useColumnChooserDismissal(gridApiRef);

  const columnDefs = useMemo(
    () => getReferenceInventoryColumnDefs(initialColumnDefinitions),
    [initialColumnDefinitions]
  );
  const serverSideDatasource = useMemo(
    () => getReferenceServerSideDatasource(getNextRowsBatch),
    [getNextRowsBatch]
  );

  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) return;
    patchContext(api, { i18nLocale });
    api.refreshHeader();
    api.refreshCells({ force: true });
  }, [i18nLocale]);

  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) return;
    patchContext(api, { users, accessControl, config });
    api.refreshCells({ force: true });
  }, [users, accessControl, config]);

  const handleGridReady = ({ api }: GridReadyEvent<ReferenceInventoryRow>) => {
    gridApiRef.current = api;
    const { openColumnSelectionPanel, getAllColumns } =
      getInventoryGridApi(api);
    registerGridApi({ openColumnSelectionPanel, getAllColumns });
  };

  return (
    <div
      className="ardoq-inventory"
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        height: '100%',
      }}
    >
      <AgGridReact<ReferenceInventoryRow>
        rowModelType="serverSide"
        serverSideDatasource={serverSideDatasource}
        context={initialContext}
        columnDefs={columnDefs}
        theme={themeQuartz}
        localeText={agGridLocaleText}
        getMainMenuItems={getReferenceColumnMainMenuItems}
        onGridReady={handleGridReady}
        onGridPreDestroyed={deregisterGridApi}
        onColumnMenuVisibleChanged={onColumnMenuVisibleChanged}
        onColumnMoved={commands.notifyColumnMoved}
        onColumnVisible={commands.notifyColumnVisible}
        suppressCsvExport
      />
    </div>
  );
};
